import React from 'react';
import { Container, Row, Col, Alert, Button } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WorkflowProvider, useWorkflow, PropertyInput } from './WorkflowProvider';
import StepIndicator from './StepIndicator';
import Step1PropertyInputStreamlined from './steps/Step1PropertyInput_Streamlined';
import Step2ProfessionalCMA from './steps/Step2ProfessionalCMA';
import Step3AIAnalysis from './steps/Step3AIAnalysis';
import Step4Dashboard from './steps/Step4Dashboard';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

interface SmartCMAWorkflowProps {
  mode?: 'consumer' | 'agent';
  hideAdvancedOptions?: boolean;
  onComplete?: (data: any) => void | Promise<void>;
  skipAuthentication?: boolean;
  autoAdvance?: boolean;
}

const WorkflowContent: React.FC<SmartCMAWorkflowProps> = ({
  mode = 'agent',
  hideAdvancedOptions = false,
  onComplete,
  skipAuthentication = false,
  autoAdvance = false
}) => {
  const { 
    state, 
    goToNextStep, 
    goToPreviousStep, 
    setError,
    setPropertyInput,
    setMLSData,
    setPredictionResult,
    setStep,
    updateWorkflowState 
  } = useWorkflow();
  const { currentStep, error } = state;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Store comparable analysis data for passing between steps and tracking
  const [comparableAnalysis, setComparableAnalysis] = React.useState<any>(null);
  const [isLoadingReport, setIsLoadingReport] = React.useState(false);
  const loadedReportRef = React.useRef<string | null>(null);
  
  // Initialize report ID from URL or clear if starting fresh
  React.useEffect(() => {
    const urlReportId = searchParams.get('reportId');
    const urlStep = searchParams.get('step');
    
    if (urlReportId) {
      // URL has a report ID - use it
      sessionStorage.setItem('currentReportId', urlReportId);
      console.log('🔗 [WORKFLOW] Set report ID from URL:', urlReportId);
    } else if (!urlReportId && !urlStep) {
      // No reportId or step in URL - starting fresh, clear session
      sessionStorage.removeItem('currentReportId');
      sessionStorage.removeItem('fullCMAResponse');
      console.log('🔗 [WORKFLOW] Starting fresh - cleared session storage');
    }
    // If there's a step but no reportId, keep current session (navigating between steps)
  }, [searchParams]);
  
  // Update URL when step changes (only if we have a reportId)
  React.useEffect(() => {
    const reportId = sessionStorage.getItem('currentReportId');
    const currentParams = Object.fromEntries(searchParams.entries());
    
    if (reportId) {
      // Only update if different from current URL
      if (currentParams.reportId !== reportId || currentParams.step !== currentStep.toString()) {
        setSearchParams({ reportId, step: currentStep.toString() });
      }
    } else if (currentParams.step !== currentStep.toString()) {
      // No reportId - just update step
      setSearchParams({ step: currentStep.toString() });
    }
  }, [currentStep, searchParams, setSearchParams]);

  // Load report data when reportId is present
  React.useEffect(() => {
    const urlReportId = searchParams.get('reportId');
    const urlStep = searchParams.get('step');
    
    if (urlReportId && !isLoadingReport && loadedReportRef.current !== urlReportId) {
      setIsLoadingReport(true);
      loadedReportRef.current = urlReportId;
      
      // Fetch report data
      const loadReportData = async () => {
        try {
          console.log('📊 [WORKFLOW] Loading report data for ID:', urlReportId);
          
          // Try smart-cma endpoint first, fall back to public CMA endpoint
          let report: any = null;

          try {
            const response = await axiosInstance.get(`/smart-cma/${urlReportId}`);
            if (response.data.success && response.data.data) {
              report = response.data.data;
              console.log('✅ [WORKFLOW] Report loaded from smart-cma:', report);
            }
          } catch (smartCmaErr) {
            console.log('⚠️ [WORKFLOW] smart-cma endpoint failed, trying public endpoint...');
          }

          // Fall back to public CMA endpoint (works for seller reports)
          if (!report) {
            try {
              const pubResponse = await axiosInstance.get(`/public/cma-report/${urlReportId}`);
              if (pubResponse.data.status === 'success' && pubResponse.data.report) {
                const pub = pubResponse.data.report;
                // Map public format to workflow format
                report = {
                  id: pub.id,
                  propertyData: {
                    address: pub.property?.address,
                    sqft: pub.property?.living_area,
                    bedrooms: pub.property?.bedrooms,
                    bathrooms: pub.property?.bathrooms,
                    yearBuilt: pub.property?.year_built,
                    propertyType: pub.property?.property_type,
                    latitude: pub.property?.latitude,
                    longitude: pub.property?.longitude
                  },
                  predictionData: {
                    value: pub.valuation?.estimated_value,
                    confidence_score: pub.valuation?.confidence_score,
                    value_range: pub.valuation?.price_range,
                    feature_importance: pub.valuation?.feature_importance
                  },
                  comparableAnalysis: {
                    comparables: pub.comparables || [],
                    statistics: pub.market_stats || {},
                    comparable_count: pub.comparable_count
                  },
                  marketContext: pub.market_analysis
                };
                console.log('✅ [WORKFLOW] Report loaded from public endpoint:', report);
              }
            } catch (pubErr) {
              console.error('❌ [WORKFLOW] Public endpoint also failed:', pubErr);
            }
          }

          if (report) {
            // Populate workflow state with saved data
            if (report.propertyData) {
              setPropertyInput({
                property_address: report.propertyData.address,
                living_area: (report.propertyData.living_area || report.propertyData.sqft || report.propertyData.livingArea || 0).toString(),
                bedrooms: (report.propertyData.bedrooms || 0).toString(),
                bathrooms: (report.propertyData.bathrooms || 0).toString(),
                year_built: (report.propertyData.year_built || report.propertyData.yearBuilt || 0).toString(),
                latitude: report.propertyData.latitude || report.propertyData.coordinates?.lat,
                longitude: report.propertyData.longitude || report.propertyData.coordinates?.lon,
                reportId: report.id
              });
            }

            // Set MLS data if available
            if (report.mlsData) {
              setMLSData(report.mlsData);
            }

            // Set prediction result
            if (report.predictionData) {
              setPredictionResult({
                success: true,
                prediction: {
                  value: report.predictionData.value || 0,
                  confidence_score: report.predictionData.confidence_score || 0,
                  value_range: report.predictionData.value_range,
                  feature_importance: report.predictionData.feature_importance
                },
                market_context: report.marketContext
              });
            }

            // Set comparable analysis if available - also store in sessionStorage for Step 2
            if (report.comparableAnalysis) {
              setComparableAnalysis(report.comparableAnalysis);
              // Store the full report data so Step 2 and Step 3 can use it without re-fetching
              const sellerData = {
                subject_property: {
                  ...report.propertyData,
                  living_area: report.propertyData.living_area || report.propertyData.sqft || 0,
                  year_built: report.propertyData.year_built || report.propertyData.yearBuilt || 0,
                  latitude: report.propertyData.latitude || report.propertyData.coordinates?.lat,
                  longitude: report.propertyData.longitude || report.propertyData.coordinates?.lon
                },
                comparable_properties: {
                  comparables: report.comparableAnalysis.comparables || [],
                  total_found: report.comparableAnalysis.comparable_count || report.comparableAnalysis.comparables?.length || 0
                },
                market_stats: report.comparableAnalysis.statistics || {},
                prediction: report.predictionData
              };
              sessionStorage.setItem('sellerReportComps', JSON.stringify(sellerData));
              // Also store in fullCMAResponse format for Step 3 compatibility
              sessionStorage.setItem('fullCMAResponse', JSON.stringify({
                success: true,
                ...sellerData,
                comparable_stats: {
                  count: sellerData.comparable_properties.total_found,
                  price_avg: sellerData.market_stats.average_sale_price || sellerData.market_stats.median_price || 0,
                  price_median: sellerData.market_stats.median_sale_price || sellerData.market_stats.median_price || 0,
                  psf_avg: sellerData.market_stats.median_ppsf || 0
                },
                ml_prediction: {
                  predicted_price: report.predictionData?.value || 0,
                  confidence_score: report.predictionData?.confidence_score || 0,
                  value_range: report.predictionData?.value_range,
                  zip_mape: report.predictionData?.xgboost_range?.zip_mape,
                  zip_r2: report.predictionData?.xgboost_range?.zip_r2,
                  mape_accuracy: report.predictionData?.xgboost_range?.zip_mape
                    ? 100 - report.predictionData.xgboost_range.zip_mape
                    : 89,
                  feature_importance: report.predictionData?.feature_importance || []
                }
              }));
            }

            // Navigate to requested step
            const targetStep = urlStep ? parseInt(urlStep) as 1 | 2 | 3 | 4 : 2;
            setStep(targetStep);

            // Mark data as loaded
            updateWorkflowState({
              isPropertyDataLoaded: true,
              propertyDataSource: 'saved_report',
              confidenceScore: report.predictionData?.confidence_score
            });
          }
        } catch (error: any) {
          console.error('❌ [WORKFLOW] Failed to load report:', error);
          console.error('❌ [WORKFLOW] Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status
          });
          
          // More specific error messages
          if (error.response?.status === 404) {
            setError('Report not found. It may have been deleted.');
          } else if (error.response?.status === 403) {
            setError('You do not have permission to view this report.');
          } else {
            setError(`Failed to load saved report: ${error.response?.data?.message || error.message || 'Unknown error'}`);
          }
        } finally {
          setIsLoadingReport(false);
        }
      };
      
      loadReportData();
    }
  }, [searchParams]); // Only re-run when URL params change

  const handleStep1Complete = async (data: PropertyInput) => {
    try {
      // Step 1 completed successfully
      
      // Enhanced validation before proceeding
      if (!data.property_address || data.property_address.trim() === '') {
        setError('Property address is required to proceed');
        return;
      }

      // Clear any previous errors
      setError(undefined);
      
      // Advance to next step
      goToNextStep();
    } catch (error) {
      console.error('❌ [WORKFLOW ERROR] Step 1 completion failed:', error);
      setError('Failed to advance to next step. Please try again.');
    }
  };

  const handleStep2Complete = async (analysis: any) => {
    try {
      // Step 2 completed successfully
      
      // Enhanced validation of analysis data
      if (!analysis || !analysis.success) {
        setError('CMA analysis failed. Please retry the analysis.');
        return;
      }

      // Check for comparables in different possible data structures
      const comparables = analysis.comparables || 
                         analysis.comparable_properties?.comparables || 
                         [];
      
      // Validate comparables found
      if (!comparables || comparables.length === 0) {
        console.warn('⚠️ No comparables found - this should not happen with KOPS-410 API');
        setError('No comparable properties found for this address. Please verify the property details and try again.');
        return;
      }

      // Store analysis data for progress tracking
      setComparableAnalysis(analysis);
      setError(undefined); // Clear any errors
      
      // Advancing to Step 3
      goToNextStep();
    } catch (error) {
      console.error('❌ [WORKFLOW ERROR] Step 2 completion failed:', error);
      setError('Failed to advance to AI analysis. Please try again.');
    }
  };

  const handleStep2Back = () => {
    // Going back to Step 1
    goToPreviousStep();
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1PropertyInputStreamlined onComplete={handleStep1Complete} />;
      case 2:
        return <Step2ProfessionalCMA onComplete={handleStep2Complete} onBack={handleStep2Back} />;
      case 3:
        return <Step3AIAnalysis />;
      case 4:
        return <Step4Dashboard />;
      default:
        return <Step1PropertyInputStreamlined onComplete={handleStep1Complete} />;
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          {/* Header with View Reports button - Always visible */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Smart CMA Analysis</h4>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => navigate('/smart-cma-reports')}
            >
              <FontAwesomeIcon icon={faList} className="me-2" />
              View My Reports
            </Button>
          </div>

          {/* Global Error Display */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(undefined)}>
              <Alert.Heading>Workflow Error</Alert.Heading>
              <p>{error}</p>
            </Alert>
          )}

          {/* Loading indicator for report data */}
          {isLoadingReport ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading report data...</span>
              </div>
              <p className="mt-3 text-muted">Loading saved report data...</p>
            </div>
          ) : (
            <>
              {/* Step Indicator */}
              <StepIndicator />
              
              {/* Current Step Content */}
              <div className="step-content">
                {renderCurrentStep()}
              </div>
              
              {/* Navigation Buttons */}
              {(currentStep > 1 || currentStep < 4) && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  {/* Previous Button */}
                  {currentStep > 1 && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => {
                        const prevStep = currentStep - 1;
                        setStep(prevStep as 1 | 2 | 3);
                        const reportId = sessionStorage.getItem('currentReportId');
                        if (reportId) {
                          setSearchParams({ reportId, step: prevStep.toString() });
                        } else {
                          setSearchParams({ step: prevStep.toString() });
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
                      Previous Step
                    </Button>
                  )}
                  
                  {/* Spacer when only one button */}
                  {(currentStep === 1 || currentStep === 4) && <div />}
                  
                  {/* Next Button */}
                  {currentStep < 4 && (
                    <Button 
                      variant="outline-primary" 
                      onClick={() => {
                        const nextStep = currentStep + 1;
                        setStep(nextStep as 2 | 3 | 4);
                        const reportId = sessionStorage.getItem('currentReportId');
                        if (reportId) {
                          setSearchParams({ reportId, step: nextStep.toString() });
                        } else {
                          setSearchParams({ step: nextStep.toString() });
                        }
                      }}
                      disabled={!state.isPropertyDataLoaded && currentStep === 1}
                    >
                      Next Step
                      <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

        </Col>
      </Row>
    </Container>
  );
};

const SmartCMAWorkflow: React.FC<SmartCMAWorkflowProps> = (props) => {
  const {
    mode = 'agent',
    hideAdvancedOptions = false,
    onComplete,
    skipAuthentication = false,
    autoAdvance = false
  } = props;

  // Breadcrumb items - only show for agent mode
  const breadcrumbItems = [
    { label: 'CRM', url: '/' },
    { label: 'Smart CMA Workflow', url: '', active: true }
  ];

  return (
    <>
      {mode === 'agent' && <PageBreadcrumb items={breadcrumbItems} />}
      <WorkflowProvider>
        <WorkflowContent
          mode={mode}
          hideAdvancedOptions={hideAdvancedOptions}
          onComplete={onComplete}
          skipAuthentication={skipAuthentication}
          autoAdvance={autoAdvance}
        />
      </WorkflowProvider>
    </>
  );
};

export default SmartCMAWorkflow; 